import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Button,
  Popover,
  makeStyles,
  Box,
  InputBase,
  IconButton,
  MenuItem,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from "@material-ui/core";
import { connect } from "react-redux";
import clsx from "clsx";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import LightTooltip from "../../common/LightTooltip";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import TruckIcon from "@material-ui/icons/LocalShipping";

import {
  checkIsFOJW,
  makeTextPlural,
  sortRouteGroupsFn
} from "../../../utils/helpers";
import {
  dragFromUnassignedToRoute,
  dragInsideRoute,
  dragFromRouteToRoute,
  unassignStops
} from "../../../store/actions/dragAndDrop";
import { setLoadingRoutesList } from "../../../store/actions/loading";
import { FormattedText } from "../../common/FormattedText";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import { routeColors } from "../../data/constants";
import { setExtendableType } from "../../../store/actions/edit";
import { setDragSelectionByArr } from "../../../store/actions/common";
import { ReactComponent as MoveTo } from "../../../assets/images/icons/move-to.svg";
import TruncatableText from "../../common/TruncatableText";

const useStyles = makeStyles(theme => ({
  btnMoveTo: {
    height: 28,
    fontSize: "0.875rem",
    textTransform: "none",
    flexShrink: 0,
    borderColor: "#ddd",
    whiteSpace: "nowrap",
    color: theme.palette.text.primary,
    padding: theme.spacing(0.75, 1, 0.75, 1.5),
    marginTop: theme.spacing(1.125),
    "& .MuiButton-label": {
      lineHeight: "1rem"
    },
    "& svg": {
      marginLeft: theme.spacing(0.5),
      width: theme.spacing(2),
      height: theme.spacing(2)
    }
  },
  iconButton: {
    width: "40px",
    height: "40px",
    "&.Mui-disabled": {
      opacity: 0.5
    }
  },
  subheader: {
    lineHeight: "0.9375rem",
    color: "#808080",
    fontSize: "0.8125rem",
    paddingBottom: theme.spacing(1)
  },
  listIcon: {
    minWidth: theme.spacing(6)
  },
  fs15px: {
    fontSize: "0.9375rem"
  },
  pl20px: {
    paddingLeft: theme.spacing(2.5)
  },
  routeWithReloadsVehicleHeader: {
    padding: theme.spacing(1),
    borderBottom: `1px solid #eee`,
    borderTop: `1px solid #eee`
  },
  routeListItem: {
    fontSize: "0.9375rem",
    cursor: "not-allowed",
    "& li": {
      height: 40,
      width: "100%"
    },
    "& li:hover": {
      backgroundColor: "#EEE"
    },
    "& li.Mui-disabled": {
      color: "#B6B6B6",
      opacity: "38%"
    }
  },
  listItem: {
    fontSize: "0.9375rem",
    height: 40,
    "&:hover": {
      backgroundColor: "#EEE"
    },
    "&.Mui-disabled": {
      color: "#B6B6B6",
      opacity: 1
    },
    "&:not(.Mui-disabled)": {
      color: theme.palette.text.primary
    }
  },
  paper: {
    maxHeight: 350,
    width: 240,
    display: "flex",
    flex: "auto",
    flexDirection: "column"
  },
  paperForFOJW: {
    maxHeight: 350,
    maxWidth: 400,
    display: "flex",
    flex: "auto",
    flexDirection: "column"
  },
  input: {
    fontSize: "0.875rem"
  },
  countIndicator: {
    display: "flex",
    position: "absolute",
    top: theme.spacing(-0.375),
    right: theme.spacing(-1.5),
    width: theme.spacing(3),
    height: theme.spacing(3)
  },
  countIndicatorLabel: {
    padding: 0
  },
  vehicleFreeStatus: {
    paddingLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.secondary.main
  },
  tooltipBody: {
    display: "flex"
  },
  tooltipKey: {
    width: "15px",
    height: "15px",
    borderRadius: "2px",
    marginLeft: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "gray"
  }
}));

function MoveToVehicle({
  routeGroups = {},
  selectedVehicles,
  dragSelection,
  isUnassigned,
  editId,
  routeId,
  dragFromRouteToRoute,
  dragFromUnassignedToRoute,
  dragInsideRoute,
  unassignStops,
  setLoadingRoutesList,
  setLoadingStop,
  setExtendableType,
  colorsObj,
  setDragSelectionByArr,
  isIcon = false
}) {
  const classes = useStyles();
  const getColor = id => (colorsObj[id] >= 0 ? colorsObj[id] : 0);

  // Effects
  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === "m") {
        setPopoverOpen(true);
      }
    };
    isIcon && window.addEventListener("keydown", event => onKeyDown(event));

    return () =>
      window.removeEventListener("keydown", event => onKeyDown(event));
  }, [isIcon]);

  // Route groups helpers
  const isRouteSelected = route => {
    let vehicle_id = route.vehicle_id;
    const _index = route.vehicle_id.indexOf("_");
    if (_index !== -1) {
      vehicle_id = vehicle_id.slice(0, _index);
    }
    return selectedVehicles.includes(vehicle_id);
  };

  // mutate and filter routeGroups object
  const generateFilteredData = () => {
    const data = {};

    Object.keys(routeGroups).forEach(groupId => {
      const group = routeGroups[groupId].map((backhaulGroup, backhaulIndex) => {
        return backhaulGroup.filter((route, index) => isRouteSelected(route));
      });
      if (group.filter(backhaulGroup => backhaulGroup.length).length) {
        data[groupId] = group;
      }
    });

    return data;
  };

  const selectedRoutes = generateFilteredData();

  const [selectedRoute, setSelectedRoute] = useState(null);

  // Search inputs
  const [searchValue, setSearchValue] = useState("");

  const handleFilter = (initialValue, inputValue) => {
    console.log(initialValue, inputValue);
    return (initialValue || "")
      .toLowerCase()
      .includes(inputValue.toLowerCase());
  };

  // Filter on search value change
  const handleChange = e => {
    let { value } = e.target;
    setSearchValue(e.target.value);
    value = value.toLowerCase();

    const filteredRoutes = {};

    Object.keys(selectedRoutes).forEach(groupId => {
      const group = selectedRoutes[groupId].map(backhaulGroup => {
        return backhaulGroup.filter(route => {
          const driverFullName = `${route.driver?.first_name} ${route.driver?.last_name}`;
          return (
            handleFilter(route.vehicle_name, value) ||
            handleFilter(driverFullName, value)
          );
        });
      });
      if (group.filter(backhaulGroup => backhaulGroup.length).length) {
        filteredRoutes[groupId] = group;
      }
    });

    setFilteredData(filteredRoutes);
  };

  const [focused, setFocused] = useState(false);

  const [filteredData, setFilteredData] = useState(selectedRoutes);

  // Popover
  const moveToRef = useRef();
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    setPopoverOpen(false);
  }, [editId]);

  useEffect(() => {
    if (popoverOpen) {
      setSearchValue("");
      setFilteredData(generateFilteredData());
      setSelectedRoute(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popoverOpen]);

  const { isSelectedRouteWithReloads, allSelectedRoutes } = useMemo(() => {
    const allSelectedRoutes = Object.values(selectedRoutes)
      .flat(2)
      .filter(
        route => route?.vehicle_id?.split("_")[0] === selectedRoute?.vehicle_id
      );

    const isSelectedRouteWithReloads = allSelectedRoutes.length > 1;

    return {
      isSelectedRouteWithReloads,
      allSelectedRoutes
    };
  }, [selectedRoute]);

  const handleMove = (index, moveToRouteId = "", sourceRouteId = "") => {
    setDragSelectionByArr("", []);
    setLoadingStop(true);
    setLoadingRoutesList(true);
    if (isNaN(index)) {
      // unassign
      unassignStops({
        sourceId: routeId,
        selection:
          dragSelection.listId &&
          dragSelection.listId.slice(6) === routeId &&
          dragSelection.list.length > 1
            ? [...dragSelection.list]
            : editId,
        isBreakInDraggingList: false,
        successMessage: "Stop unscheduled"
      });
    } else if (isUnassigned) {
      // unassigned -> route
      dragFromUnassignedToRoute({
        destinationId: selectedRoute.id,
        destIndex: index,
        selection: editId,
        successMessage: `Stop${makeTextPlural(editId.length)} moved to ${
          selectedRoute.vehicle_name
        }`
      });
    } else if (
      (selectedRoute.id === routeId && !isSelectedRouteWithReloads) ||
      (moveToRouteId && moveToRouteId === sourceRouteId)
    ) {
      // route -><- route
      dragInsideRoute({
        sourceId: routeId,
        destIndex: !isSelectedRouteWithReloads ? index - 1 : index,
        selection:
          dragSelection.listId &&
          dragSelection.listId.slice(6) === routeId &&
          dragSelection.list.length > 1
            ? [...dragSelection.list]
            : editId,
        successMessage: `Stop${makeTextPlural(editId.length)} moved to ${
          selectedRoute.vehicle_name
        }`
      });
    } else {
      // route1 -> route2
      dragFromRouteToRoute({
        sourceId: sourceRouteId ? sourceRouteId : routeId,
        destinationId: moveToRouteId ? moveToRouteId : selectedRoute.id,
        destIndex: index,
        selection:
          dragSelection.listId &&
          dragSelection.listId.slice(6) ===
            (sourceRouteId ? sourceRouteId : routeId) &&
          dragSelection.list.length > 1
            ? [...dragSelection.list]
            : editId,
        isBreakInDraggingList: false,
        successMessage: `Stop${makeTextPlural(editId.length)} moved to ${
          selectedRoute.vehicle_name
        }`
      });
    }
    setExtendableType(null);
    setPopoverOpen(false);
  };
  const isFOJW = checkIsFOJW();

  return (
    <>
      <Box position="relative">
        {isIcon ? (
          <OptimizedTooltip
            placement="bottom"
            title={
              <Box className={classes.tooltipBody}>
                Move to
                <Box className={classes.tooltipKey}>M</Box>
              </Box>
            }
          >
            <span>
              <IconButton
                onClick={() => setPopoverOpen(true)}
                className={classes.iconButton}
                ref={moveToRef}
              >
                <MoveTo />
              </IconButton>
            </span>
          </OptimizedTooltip>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPopoverOpen(true)}
            ref={moveToRef}
            className={classes.btnMoveTo}
          >
            Move to <ExpandMoreIcon />
          </Button>
        )}
        {dragSelection.listId &&
          dragSelection.listId.slice(6) === routeId &&
          dragSelection.list.length > 1 && (
            <Chip
              className={classes.countIndicator}
              classes={{
                label: classes.countIndicatorLabel
              }}
              color="primary"
              label={dragSelection.list.length}
            />
          )}
      </Box>
      <Popover
        open={popoverOpen}
        classes={{
          paper: isFOJW ? classes.paperForFOJW : classes.paper
        }}
        anchorEl={moveToRef.current}
        onClose={() => {
          setPopoverOpen(false);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <Box
          display="flex"
          style={{ backgroundColor: "#f7f7f7" }}
          position="sticky"
          top="0"
          borderBottom="1px solid #EEE"
          zIndex="1"
          height={48}
          flexShrink={0}
        >
          {selectedRoute ? (
            <>
              {/* Stops header */}
              <Box display="flex" alignItems="center" pl={1} pr={2}>
                <IconButton onClick={() => setSelectedRoute(null)}>
                  <ArrowBackIcon />
                </IconButton>
              </Box>
              <Box display="flex" alignItems="center" pr={2}>
                <Typography variant="body2">Choose stop location</Typography>
              </Box>
            </>
          ) : (
            <>
              {/* Vehicles header */}
              {!Object.keys(selectedRoutes).length && (
                <LightTooltip
                  text={`Add a vehicle to be able to search`}
                  fromRight={true}
                />
              )}
              <OptimizedTooltip placement="bottom" title="Search">
                <Box display="flex" alignItems="center" pl={2} pr={3}>
                  <SearchIcon color={focused ? "primary" : "action"} />
                </Box>
              </OptimizedTooltip>
              <Box
                flexGrow="1"
                display="flex"
                pr={2}
                alignItems="center"
                style={{
                  pointerEvents: Object.keys(selectedRoutes).length
                    ? "all"
                    : "none"
                }}
              >
                <InputBase
                  className={classes.input}
                  placeholder={`Search vehicle`}
                  inputProps={{ "aria-label": `search vehicle` }}
                  value={searchValue || ""}
                  onChange={handleChange}
                  name="vehicle"
                  style={{ width: "100%" }}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                />
              </Box>
              {!!searchValue.length && (
                <OptimizedTooltip placement="bottom" title="Clear">
                  <Box display="flex" alignItems="center" pr={1}>
                    <IconButton
                      className={classes.closeIcon}
                      onClick={() => {
                        setFilteredData(selectedRoutes);
                        setSearchValue("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </OptimizedTooltip>
              )}
            </>
          )}
        </Box>

        {filteredData &&
          (selectedRoute ? (
            <Box pt={1} pb={2} overflow="auto">
              {/* Stops */}

              {selectedRoute.stops.length === 2 &&
                !isSelectedRouteWithReloads && (
                  <MenuItem
                    onClick={() => handleMove(1)}
                    disabled={selectedRoute.stops[1].id === editId}
                    classes={{
                      root: clsx(classes.listItem, classes.pl20px)
                    }}
                    data-cy="stop-moveToIndex-1"
                  >
                    1
                  </MenuItem>
                )}

              {allSelectedRoutes.map((route, routeIndex) => {
                let passedBreak = false;
                let currentStopId;

                return route.stops
                  .filter(
                    task => !["start", "end", "reload"].includes(task.type)
                  )
                  .map((task, i, arr) => {
                    if (arr[i + 1] && arr[i + 1].type === "break") {
                      passedBreak = true;
                      return null;
                    }
                    let isCurrentStop;

                    if (editId.includes(arr[i] && arr[i].id)) {
                      isCurrentStop = true;
                      currentStopId = task.id;
                    }
                    const indexToShow = !passedBreak ? i + 1 : i + 2;
                    const indexToMove = i + 1;

                    const handleMoveTo = () => {
                      // IN 1.15 reloads are slices of full route
                      if (isSelectedRouteWithReloads) {
                        let moveToRouteId;
                        let sourceRouteId;
                        let moveToIndex;
                        allSelectedRoutes.forEach(route => {
                          route.stops
                            .filter(stop => stop.type !== "reload")
                            .forEach((stop, index) => {
                              if (stop.id === task.id) {
                                moveToIndex = index;
                                moveToRouteId = route.id;
                              }
                              if (currentStopId === stop.id) {
                                sourceRouteId = route.id;
                              }
                            });
                        });
                        handleMove(moveToIndex, moveToRouteId, sourceRouteId);
                      } else {
                        handleMove(indexToMove);
                      }
                    };

                    if (i === arr.length && route.id === routeId) return null;

                    return (
                      <>
                        {isSelectedRouteWithReloads && i === 0 && (
                          <>
                            <Typography
                              classes={{
                                root: clsx(
                                  classes.routeWithReloadsVehicleHeader
                                )
                              }}
                              variant="h6"
                            >{`${selectedRoute.vehicle_name}, Route ${
                              routeIndex + 1
                            }`}</Typography>
                          </>
                        )}
                        <MenuItem
                          key={task.id}
                          onClick={handleMoveTo}
                          disabled={isCurrentStop}
                          classes={{
                            root: clsx(classes.listItem, classes.pl20px)
                          }}
                          data-cy={`stop-moveToIndex-${indexToShow}`}
                        >
                          {`${indexToShow} ${
                            isCurrentStop ? "(current location)" : ""
                          }`}
                        </MenuItem>
                      </>
                    );
                  });
              })}
            </Box>
          ) : (
            <Box pt={2} overflow="auto">
              {/* Vehicles (filtered) */}

              {Object.keys(filteredData).length ? (
                Object.keys(filteredData)
                  .sort(sortRouteGroupsFn)
                  .map(groupId => {
                    return (
                      <Box key={groupId} pb={3}>
                        {/* <ListSubheader
                          disableSticky
                          className={classes.subheader}
                        >
                          {secondsToTime(groupId.split("-")[0])}-
                          {secondsToTime(groupId.split("-")[1])}
                        </ListSubheader> */}
                        {filteredData[groupId].map(backhaulGroup => {
                          return (
                            backhaulGroup.length > 0 &&
                            [backhaulGroup[0]].map(route => {
                              const textToShow = `${route.vehicle_name}`;
                              const driverFullName = `${route?.driver?.first_name} ${route?.driver?.last_name}`;
                              return (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  key={route.id}
                                  className={classes.routeListItem}
                                >
                                  <MenuItem
                                    onClick={() => setSelectedRoute(route)}
                                    disabled={route.locked}
                                    data-cy={`stop-moveToRoute-${route.vehicle_name
                                      .split(" ")
                                      .join("")}`}
                                  >
                                    <>
                                      <ListItemIcon
                                        className={classes.listIcon}
                                      >
                                        <TruckIcon
                                          style={{
                                            color:
                                              routeColors[
                                                getColor(route.id) %
                                                  routeColors.length
                                              ]
                                          }}
                                        />
                                      </ListItemIcon>
                                    </>
                                    {isFOJW &&
                                      route?.driver?.first_name?.length > 0 && (
                                        <>
                                          <Typography variant="body2">
                                            <TruncatableText
                                              maxWidth={240}
                                              value={driverFullName}
                                            >
                                              <FormattedText
                                                text={driverFullName}
                                                query={searchValue}
                                              />
                                            </TruncatableText>
                                          </Typography>
                                        </>
                                      )}
                                    {isFOJW &&
                                      route?.driver?.first_name?.length > 0 && (
                                        <span
                                          style={{
                                            marginRight: 4,
                                            marginLeft: 4
                                          }}
                                        >
                                          •
                                        </span>
                                      )}
                                    <>
                                      <Typography className={classes.fs15px}>
                                        <TruncatableText
                                          maxWidth={150}
                                          value={textToShow}
                                        >
                                          <FormattedText
                                            text={textToShow}
                                            query={searchValue}
                                          />
                                        </TruncatableText>
                                      </Typography>
                                    </>
                                  </MenuItem>
                                </Box>
                              );
                            })
                          );
                        })}
                      </Box>
                    );
                  })
              ) : (
                <Box px={2} pb={2}>
                  <Typography>No vehicles found</Typography>
                </Box>
              )}
            </Box>
          ))}
        <Box position="sticky" bottom="0" py={1} borderTop="1px solid #EEE">
          <ListItem
            button
            onClick={() => handleMove(NaN)}
            disabled={isUnassigned}
            classes={{ root: classes.listItem }}
          >
            <ListItemIcon className={classes.listIcon}>
              <EventBusyIcon />
            </ListItemIcon>
            <ListItemText
              primary="Unscheduled stops"
              classes={{ primary: classes.fs15px }}
            />
          </ListItem>
        </Box>
      </Popover>
    </>
  );
}

const mapStateToProps = state => ({
  selectedVehicles: state.project.selectedVehicles,
  colorsObj: state.project.solution && state.project.solution.routeColors,
  dragSelection: {} //state.common.dragSelection,
});

export default connect(mapStateToProps, {
  dragFromRouteToRoute,
  dragFromUnassignedToRoute,
  dragInsideRoute,
  unassignStops,
  setLoadingRoutesList,
  setExtendableType,
  setDragSelectionByArr
})(MoveToVehicle);
